import DevJSONView from '@/components/DevJSONView';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { Box, Button, Stack, Switch, Tooltip, Typography, useTheme } from '@mui/material';
import { useGetFormConfigQuery, useSaveFormConfigMutation } from '@/features/config/configApi';
import DataGridNumericFormat from '@/components/table/DataGridComponents/DataGridNumericFormat';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import DataGridMultilinePopper from '@/components/table/DataGridComponents/DataGridMultilinePopper';
import {
	COPY_FORM_CONFIG_DIALOG,
	CopyFormConfigDialog,
} from '@/components/dialogs/CopyFormConfigDialog';
import { openDialog } from '@/features/dialog/dialogSlice';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { isProspectFieldVisible } from '@/features/config/helper';
import { SCOPES } from '@/features/config/scopes';
import {
	SHARE_FORM_CONFIG_DIALOG,
	ShareFormConfigDialog,
} from '@/components/dialogs/ShareFormConfigDialog';
import ResetFormConfigButton from './ResetFormConfigButton';
import SharedCell from './SharedCell';

const SHARED_ROW_TOOLTIP =
	'This row is shared from another broker group. Break the link to edit it.';

// A shared row follows its original and can't be edited
const CellSwitch = ({ disabled, shared, checked, onChange, tooltipTitle }) => {
	return (
		<Tooltip title={shared ? SHARED_ROW_TOOLTIP : tooltipTitle}>
			<div>
				<Switch
					size='small'
					disabled={disabled || shared}
					checked={checked}
					onChange={onChange}
					slotProps={{ input: { 'aria-label': 'controlled' } }}
				/>
			</div>
		</Tooltip>
	);
};

const CONFIG_TYPE = 'client';

const ClientConfigTable = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	// Ticked by field name, so a tick stays on the field when an edit gives it a new config id
	const [selectedNames, setSelectedNames] = useState([]);
	const [sortModel, setSortModel] = useState([
		{
			field: 'group',
			sort: 'desc',
		},
	]);

	const isCopyOpen = useSelector((state) => state.dialog.open[COPY_FORM_CONFIG_DIALOG]);
	const isShareOpen = useSelector((state) => state.dialog.open[SHARE_FORM_CONFIG_DIALOG]);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const { getFeatureFlags } = useFeatureFlagContext();
	const editConfigTable = Boolean(getFeatureFlags('editConfigTable')?.active);

	const canCopy = Boolean(userDetails?.canViewGroup) && editConfigTable;

	const columnVisibilityModel = useMemo(
		() => ({
			...(!canCopy && { shared: false }),
		}),
		[canCopy],
	);

	const configQuery = useGetFormConfigQuery(
		{ configType: CONFIG_TYPE, includeSharedWith: true },
		{ skip: !userDetails },
	);

	const rows = configQuery.data ?? [];
	const selectedConfigs = rows.filter((row) => selectedNames.includes(row.name));
	// Only broker group configs can be shared; ledger and default rows are copied instead
	const shareableConfigs = selectedConfigs.filter((row) => row.scope === SCOPES.BROKER_GROUP);

	const [saveConfig, { isLoading: isUpdateLoading }] = useSaveFormConfigMutation();

	// Copies the given rows into the user's other groups, with their current settings (ids from
	// the latest rows, so an edited row copies the edited config). With none given, copies only
	// the rows customised for the user's broker group; the rest keep following the ledger or
	// default in the other groups too.
	const openCopyDialog = (configs) => {
		const docIds = (
			configs.length
				? configs
				: rows.filter(
						(r) =>
							r.scope === SCOPES.BROKER_GROUP &&
							r.scopeId === userDetails?.brokerGroupId,
					)
		).map((r) => r.id);
		if (!docIds.length) {
			dispatch(
				showToast({
					message: 'Nothing to copy: no fields are customised for your broker group yet.',
					error: true,
				}),
			);
			return;
		}
		dispatch(
			openDialog({
				dialogName: COPY_FORM_CONFIG_DIALOG,
				data: { docIds, configType: CONFIG_TYPE },
			}),
		);
	};

	// Shares the ticked broker group rows; a shared row shares its original, so its original's
	// group (like the rows' own) can't be picked
	const openShareDialog = () =>
		dispatch(
			openDialog({
				dialogName: SHARE_FORM_CONFIG_DIALOG,
				data: {
					docIds: shareableConfigs.map((r) => r.id),
					configType: CONFIG_TYPE,
					originalGroupIds: [
						...new Set(shareableConfigs.map((r) => r.sharedFrom?.scopeId ?? r.scopeId)),
					],
					skipped: selectedConfigs.length - shareableConfigs.length,
				},
			}),
		);

	const handleSave = async ({ id, configType, ...properties }) => {
		console.log('Saving config doc', id, properties);
		if (Object.keys(properties ?? {}).length === 0) return;
		return await saveConfig({
			docId: id,
			properties,
			configType,
		})
			.unwrap()
			.then((res) => {
				console.log('Update config res', res);
				dispatch(
					showToast({
						message: 'Successfully updated config',
						success: true,
					}),
				);
				return true;
			})
			.catch((e) => {
				console.error(`Error updating config ${id}`, e);
				dispatch(
					showToast({
						message: 'Error updating config; please try again.',
						error: true,
					}),
				);
				return false;
			});
	};

	const disabled = isUpdateLoading;
	const isLoading = configQuery.isLoading || disabled;

	const columns = [
		{
			field: 'label',
			headerName: 'Label',
			flex: 2,
			minWidth: 200,
			editable: true,
			sortable: true,
			renderHeader: (params) => {
				return (
					<Tooltip title='The label used in the Create Client form'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => {
				return (
					<Tooltip title={row.description}>
						<span>{row.label}</span>
					</Tooltip>
				);
			},
			display: 'flex',
		},
		{
			field: 'mandatory.client',
			headerName: 'Required (client)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether or not this field is mandatory for clients. Some fields are required by Insight as a default and cannot be changed.'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Required'}
							</Typography>
							<Typography variant='task_updated'>{'Client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled =
					!editConfigTable || (row?.mandatory?.client && !row.optional) || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;

				return (
					<CellSwitch
						disabled={disabled}
						shared={Boolean(row.sharedFrom)}
						checked={row?.mandatory?.client}
						onChange={async (e) => {
							const checked = e.target.checked;
							const mandatory = {
								...row.mandatory,
								client: e.target.checked,
							};

							const success = await handleSave({
								id: params.row.id,
								mandatory,
								...(checked && {
									visible: {
										...row.visible,
										create: true,
									},
								}),
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'This field is required by default and cannot be changed'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'mandatory.prospect',
			headerName: 'Required (prospect)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether or not this field is mandatory for prospects. Some fields are required by Insight as a default and cannot be changed.'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Required'}
							</Typography>
							<Typography variant='task_updated'>{'Prospect'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled =
					!editConfigTable || (row?.mandatory?.prospect && !row.optional) || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;
				return (
					<CellSwitch
						disabled={disabled}
						shared={Boolean(row.sharedFrom)}
						checked={row?.mandatory?.prospect}
						onChange={async (e) => {
							const checked = e.target.checked;
							const mandatory = {
								...row.mandatory,
								prospect: checked,
							};
							const success = await handleSave({
								id: row.id,
								mandatory,
								...(checked && {
									visible: {
										...row.visible,
										prospect: { create: true, update: true },
									},
								}),
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'This field is required by default and cannot be changed'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'visible.create',
			headerName: 'Visible (create)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether this field is visible in the Create Client form'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{'Create client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled = !editConfigTable || row?.mandatory?.client || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;
				return (
					<CellSwitch
						disabled={disabled}
						shared={Boolean(row.sharedFrom)}
						checked={row?.visible?.create}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								create: e.target.checked,
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'To change the visibility of this field, set it to non-required for Client'
								: ''
						}
					/>
				);
			},
		},
		...[
			{ view: 'create', form: 'Create' },
			{ view: 'update', form: 'Update' },
		].map(({ view, form }) => ({
			field: `visible.prospect.${view}`,
			headerName: `Visible (${view} prospect)`,
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: () => {
				return (
					<Tooltip
						title={`Whether this field is visible in the ${form} Client form for prospects`}
					>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{`${form} prospect`}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => {
				const disabled = !editConfigTable || row?.mandatory?.prospect || isLoading;
				const prospect = {
					create: isProspectFieldVisible(row, 'create'),
					update: isProspectFieldVisible(row, 'update'),
				};
				return (
					<CellSwitch
						disabled={disabled}
						shared={Boolean(row.sharedFrom)}
						checked={prospect[view]}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								prospect: { ...prospect, [view]: e.target.checked },
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'To change the visibility of this field, set it to non-required for Prospect'
								: ''
						}
					/>
				);
			},
		})),
		{
			field: 'visible.edit',
			headerName: 'Visible (edit)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether this field is visible in the Client Details section'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{'Update client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;

				return (
					<CellSwitch
						disabled={!editConfigTable}
						shared={Boolean(row.sharedFrom)}
						checked={row?.visible?.update}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								update: e.target.checked,
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
					/>
				);
			},
		},
		{
			field: 'group',
			headerName: 'Group',
			flex: 1.2,
			minWidth: 120,
			editable: false,
			sortable: true,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Used to group similar fields together'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
		},
		// {
		// 	field: 'order',
		// 	headerName: 'Order',
		// 	flex: 0.5,
		// 	minWidth: 50,
		// 	editable: true,
		// 	sortable: true,
		// 	display: 'flex',
		// 	renderHeader: (params) => {
		// 		return (
		// 			<Tooltip title='Which order to render this field within its Group'>
		// 				<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
		// 					{params.colDef.headerName}
		// 				</Typography>
		// 			</Tooltip>
		// 		);
		// 	},
		// 	renderEditCell: (params) => <DataGridNumericFormat {...params} />,
		// },
		{
			field: 'name',
			headerName: 'Field Name',
			flex: 2,
			minWidth: 200,
			editable: false,
			sortable: true,
			renderHeader: (params) => {
				return (
					<Tooltip title='The internal name of the field in Insight or Firebase'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => {
				return (
					<Tooltip title={row.description}>
						<span>{row.name}</span>
					</Tooltip>
				);
			},
			display: 'flex',
		},
		{
			field: 'helperText',
			headerName: 'Helper Text',
			flex: 2,
			minWidth: 200,
			editable: true,
			sortable: false,
			renderHeader: (params) => {
				return (
					<Tooltip title='The helper text the user sees; leave blank to not display helper text'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderEditCell: (params) => {
				return <DataGridMultilinePopper {...params} newLine={false} />;
			},
			display: 'flex',
		},
		{
			field: 'shared',
			headerName: 'Shared',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title="Rows shared from another broker group follow its changes and can't be edited until you break the link. Rows you've shared change those groups too when you edit them.">
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => <SharedCell config={row} disabled={isLoading} />,
		},
	];

	return (
		<Stack
			direction='column'
			spacing={1}
			sx={{ height: '75vh', maxHeight: '100%', minHeight: '75vh' }}
		>
			{canCopy && (
				<Stack direction='row' spacing={1} sx={{ justifyContent: 'flex-end', p: 1 }}>
					<ResetFormConfigButton
						configs={selectedConfigs}
						configType={CONFIG_TYPE}
						disabled={isLoading}
					/>
					{shareableConfigs.length > 0 && (
						<Tooltip title="Share the ticked broker group rows with your other broker groups. They follow your changes and can't be edited there until the link is broken.">
							<Box>
								<Button
									variant='table_actions'
									disabled={isLoading}
									onClick={(e) => {
										e.stopPropagation();
										openShareDialog();
									}}
								>
									{`Share (${shareableConfigs.length})`}
								</Button>
							</Box>
						</Tooltip>
					)}
					<Tooltip
						title={
							selectedConfigs.length
								? 'Copy the ticked rows to your other broker groups, replacing their settings for these fields'
								: "Copy every row customised for your broker group to your other broker groups, replacing their settings for those fields. Rows that follow the ledger or default aren't copied."
						}
					>
						<Box>
							<Button
								variant='table_actions'
								disabled={isLoading}
								onClick={(e) => {
									e.stopPropagation();
									openCopyDialog(selectedConfigs);
								}}
							>
								{selectedConfigs.length
									? `Copy (${selectedConfigs.length})`
									: 'Copy full config'}
							</Button>
						</Box>
					</Tooltip>
				</Stack>
			)}

			<DataGrid
				sx={{
					'& .MuiDataGrid-editInputCell': {
						backgroundColor: 'transparent',
					},
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-cell': {
						padding: 1,
						minHeight: '42px',
					},
					'& .Mui-disabled .MuiDataGrid-cell': {
						backgroundColor: theme.palette.action.disabledBackground,
						color: theme.palette.text.disabled,
					},
					'& .Mui-disabled .MuiDataGrid-row': {
						backgroundColor: 'transparent',
					},
					border: 'none',
				}}
				getRowClassName={(params) => (disabled ? 'Mui-disabled' : '')}
				columnVisibilityModel={columnVisibilityModel}
				loading={configQuery.isLoading}
				slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
				width='100%'
				getRowHeight={() => 'auto'}
				getRowId={(row) => row.name}
				{...(canCopy && { checkboxSelection: true })}
				disableRowSelectionOnClick
				processRowUpdate={async (newRow, oldRow) => {
					console.log('processRowUpdate', { newRow, oldRow });
					if (newRow.order !== oldRow.order) {
						const amount = rows.filter((r) => r.group === oldRow.group).length;
						newRow.order = Math.min(amount, Math.max(newRow.order, 0));
					}
					const { configType } = newRow;
					const update = Object.entries(newRow).reduce(
						(acc, [key, value]) => {
							if (oldRow[key] !== value && value != null) {
								acc[key] = value;
							}
							return acc;
						},
						{ id: oldRow.id, configType },
					);
					const success = await handleSave(update);
					return success ? newRow : oldRow;
					// return newRow;
				}}
				rows={rows}
				columns={columns}
				isCellEditable={(params) => {
					// A shared row follows its original and can't be edited
					if (!editConfigTable || isLoading || params.row.sharedFrom) return false;
					return params.colDef.editable;
					// if (!params.colDef.editable) return false;
					// return false;
				}}
				onRowSelectionModelChange={setSelectedNames}
				initialState={{
					sorting: {
						sortModel,
					},
				}}
				sortingOrder={['desc', 'asc']}
				rowSelectionModel={selectedNames}
				disableColumnResize
				disableColumnFilter
				disableColumnSelector
				disableColumnMenu
				onRowClick={(_, e) => {
					e.stopPropagation();
				}}
				hideFooter
				slots={{
					noRowsOverlay: configQuery.isError
						? () => <NoRowsErrorOverlay objectType={'client config documents'} />
						: () => <NoRowsOverlay objectType={'client config documents'} />,
				}}
				sortModel={sortModel}
				onSortModelChange={(newModel) => setSortModel(newModel)}
			/>
			<DevJSONView value={{ selectedNames }} />
			{isCopyOpen && <CopyFormConfigDialog />}
			{isShareOpen && <ShareFormConfigDialog />}
		</Stack>
	);
};

export default ClientConfigTable;
